// components/ReadMoreTextBase.jsx
import React, { useMemo, useState } from 'react';
import DOMPurify from 'dompurify';

/**
 * Pure read-more renderer: no Firestore, no routing.
 * Props:
 *  - text (string, may contain HTML)
 *  - wordLimit (number)
 *  - readMore (boolean)
 *  - toggleWordInactive / toggleWordActive (strings)
 *  - plainText (boolean) -> render as plain text paragraph/span
 */
export default function ReadMoreTextBase({
  text = '',
  wordLimit = 20,
  readMore = true,
  toggleWordInactive = 'Read more',
  toggleWordActive = 'Read less',
  plainText = false,
  className = 'text-muted',
  onToggle, // optional callback(isExpanded)
}) {
  const [isExpanded, setIsExpanded] = useState(false);

  // Sanitize once
  const sanitized = useMemo(() => DOMPurify.sanitize(text || ''), [text]);

  // Robust plain-text extraction (SSR-safe)
  const plain = useMemo(() => {
    if (typeof window !== 'undefined') {
      const tmp = document.createElement('div');
      tmp.innerHTML = sanitized;
      return (tmp.textContent || tmp.innerText || '').trim();
    }
    return sanitized.replace(/<[^>]+>/g, '').trim();
  }, [sanitized]);

  const words = useMemo(() => (plain ? plain.split(/\s+/) : []), [plain]);
  const needsMore = words.length > wordLimit;
  const preview = words.slice(0, wordLimit).join(' ');
  const display = isExpanded || !needsMore ? plain : `${preview}…`;

  const handleToggle = () => {
    const next = !isExpanded;
    setIsExpanded(next);
    onToggle?.(next);
  };

  const Toggle = () =>
    readMore && needsMore ? (
      <>
        {' '}
        <span
          className='primary-hover fs-10'
          style={{ fontWeight: 'bold', cursor: 'pointer' }}
          onClick={handleToggle}
        >
          {isExpanded ? toggleWordActive : toggleWordInactive}
        </span>
      </>
    ) : null;

  if (plainText) {
    return (
      <span className={className}>
        {display}
        <Toggle />
      </span>
    );
  }

  return (
    <p className={className}>
      <span>{display}</span>
      <Toggle />
      <br />
    </p>
  );
}
